import React from "react";
import LegalLayout from "../components/LegalLayout";

const NAV = [
  { id: "intro", label: "1. Introduction" },
  { id: "collect", label: "2. Information We Collect" },
  { id: "use", label: "3. How We Use Information" },
  { id: "cookies", label: "4. Cookies" },
  { id: "share", label: "5. Third Parties & Sharing" },
  { id: "security", label: "6. Data Security" },
  { id: "retention", label: "7. Data Retention" },
  { id: "rights", label: "8. Your Rights" },
  { id: "children", label: "9. Children's Privacy" },
  { id: "links", label: "10. Third-Party Links" },
  { id: "contact", label: "11. Contact" },
  { id: "grievance", label: "12. Grievance Redressal" },
  { id: "transfers", label: "13. International Transfers" },
  { id: "changes", label: "14. Changes" },
  { id: "definitions", label: "15. Definitions" },
  { id: "ack", label: "16. Acknowledgment" },
];

const Privacy = () => (
  <LegalLayout
    eyebrow="Gruham · Legal"
    title="Privacy Policy"
    lastUpdated="5 June 2026"
    nav={NAV}
  >
    <LegalLayout.Section id="intro" title="1. Introduction & Commitment to Privacy">
      <p>
        Gruham ("we," "us," "our," or "Company") respects your privacy
        and is committed to protecting your personal data. This Privacy Policy
        explains how we collect, use, disclose and safeguard your information
        when you visit our e-commerce website and mobile applications
        (collectively, the "Platform").
      </p>
      <p>
        This Privacy Policy complies with the Information Technology Act, 2000,
        the IT Rules, 2011, the Consumer Protection Act, 2019 and the Digital
        Personal Data Protection Act, 2023.
      </p>
    </LegalLayout.Section>

    <LegalLayout.Section id="collect" title="2. Information We Collect">
      <p className="font-semibold">2.1 Information You Provide Directly</p>
      <ul className="list-disc pl-5 space-y-1">
        <li>Account: name, email, phone, shipping/billing address, DOB.</li>
        <li>Purchase: product preferences, order history, delivery preferences.</li>
        <li>Communication: messages via email, chat, WhatsApp, phone, reviews and feedback.</li>
        <li>Payment: handled securely through payment gateways – we do NOT store complete payment details.</li>
      </ul>
      <p className="font-semibold">2.2 Information Collected Automatically</p>
      <ul className="list-disc pl-5 space-y-1">
        <li>Device, OS, browser, IP, device identifiers.</li>
        <li>Browsing activity, search queries, time spent.</li>
        <li>Approximate location (IP), precise GPS only with permission.</li>
        <li>Transaction history.</li>
      </ul>
      <p className="font-semibold">2.3 Information from Third Parties</p>
      <ul className="list-disc pl-5 space-y-1">
        <li>Payment gateways, logistics partners and analytics platforms.</li>
        <li>Social media platforms if you sign in using them.</li>
      </ul>
    </LegalLayout.Section>

    <LegalLayout.Section id="use" title="3. How We Use Your Information">
      <p className="font-semibold">3.1 Essential Service Functions</p>
      <ul className="list-disc pl-5 space-y-1">
        <li>Process orders, manage accounts, provide support.</li>
        <li>Process payments through secure gateways.</li>
        <li>Facilitate delivery, tracking, returns and refunds.</li>
        <li>Verify identity and prevent fraud.</li>
      </ul>
      <p className="font-semibold">3.2 Marketing & Personalization</p>
      <ul className="list-disc pl-5 space-y-1">
        <li>Send promotional emails / SMS / WhatsApp with your consent.</li>
        <li>Personalize shopping experience and recommendations.</li>
      </ul>
      <p className="font-semibold">3.3 Analytics & Improvement</p>
      <p>Analyze usage to improve UX, identify trends and optimize features.</p>
      <p className="font-semibold">3.4 Legal & Compliance</p>
      <p>Comply with legal obligations, court orders, government requests; prevent fraud.</p>
    </LegalLayout.Section>

    <LegalLayout.Section id="cookies" title="4. Cookies & Similar Technologies">
      <p>
        We use cookies, web beacons, pixels and similar tracking technologies to
        remember your login, track shopping behaviour, deliver personalized
        content, analyze performance and measure marketing campaigns.
      </p>
      <ul className="list-disc pl-5 space-y-1">
        <li><strong>Session cookies:</strong> expire when you close your browser.</li>
        <li><strong>Persistent cookies:</strong> remain for extended periods.</li>
        <li><strong>Essential cookies:</strong> required for basic functionality.</li>
        <li><strong>Analytical cookies:</strong> help us understand usage.</li>
        <li><strong>Marketing cookies:</strong> enable personalized advertising.</li>
      </ul>
      <p>
        You can manage cookies through your browser settings. Disabling cookies
        may affect Platform functionality.
      </p>
    </LegalLayout.Section>

    <LegalLayout.Section id="share" title="5. Third-Party Services & Data Sharing">
      <p><strong>We do NOT sell your personal data.</strong></p>
      <p className="font-semibold">5.1 Service Providers</p>
      <ul className="list-disc pl-5 space-y-1">
        <li>Payment processors (Razorpay, PayU and similar).</li>
        <li>Shipping & logistics partners.</li>
        <li>Email / SMS / WhatsApp gateway services.</li>
        <li>Analytics & cloud hosting providers.</li>
      </ul>
      <p className="font-semibold">5.2 Marketing & Advertising Partners</p>
      <p>Facebook, Instagram, Google for targeted advertising; affiliate networks.</p>
      <p className="font-semibold">5.3 When Required by Law</p>
      <p>Government authorities, law enforcement, legal proceedings.</p>
      <p className="font-semibold">5.4 Business Transfers</p>
      <p>
        If Gruham is involved in a merger, acquisition or sale, your
        information may be transferred. Notice will be provided.
      </p>
    </LegalLayout.Section>

    <LegalLayout.Section id="security" title="6. Data Security & Protection">
      <p>
        We implement SSL/TLS encryption, firewalls, password hashing, secure
        payment processing and database encryption. Access is controlled on a
        need-to-know basis, employees are trained on data protection and we
        run regular security audits.
      </p>
      <p>
        While we implement robust measures, no transmission over the Internet
        is 100% secure. You use the Platform at your own risk and should use
        strong, unique passwords.
      </p>
    </LegalLayout.Section>

    <LegalLayout.Section id="retention" title="7. Data Retention">
      <ul className="list-disc pl-5 space-y-1">
        <li>Account data: duration of your account and as required by law (~5 years).</li>
        <li>Transaction data: at least 7 years per Indian tax regulations.</li>
        <li>Marketing data: retained until you unsubscribe.</li>
        <li>Analytics & logs: 12-24 months.</li>
        <li>Deletion requests processed within 30 days; archived backups per backup policy.</li>
      </ul>
    </LegalLayout.Section>

    <LegalLayout.Section id="rights" title="8. Your Rights & Choices">
      <ul className="list-disc pl-5 space-y-1">
        <li>Right to access your personal data (within 30 days).</li>
        <li>Right to rectification of inaccurate or incomplete data (within 15 days).</li>
        <li>Right to erasure (right to be forgotten), where not legally required to retain.</li>
        <li>Right to withdraw consent for marketing communications.</li>
        <li>Right to opt-out of cookies (except essential cookies).</li>
        <li>Right to lodge a complaint with the appropriate authority.</li>
      </ul>
      <p>
        To exercise any of the above rights, write to support@gruham.in
        with your full name, registered email, request details and a copy of
        government-issued ID for verification.
      </p>
    </LegalLayout.Section>

    <LegalLayout.Section id="children" title="9. Children's Privacy">
      <p>
        Gruham is not intended for use by children under 18 years of
        age. We do not knowingly collect personal data from children. If we
        discover such data, we will delete it promptly.
      </p>
    </LegalLayout.Section>

    <LegalLayout.Section id="links" title="10. Links to Third-Party Websites">
      <p>
        Our Platform may contain links to third-party websites and services
        that are not operated by Gruham This Privacy Policy does not
        apply to those external sites.
      </p>
    </LegalLayout.Section>

    <LegalLayout.Section id="contact" title="11. Contact Information & Data Protection Officer">
      <ul className="list-disc pl-5 space-y-1">
        <li>Company: Gruham</li>
        <li>Address: Copper Build, Police Parade ground road, Nana Mauva, Rajkot, Gujarat, 360005, India.</li>
        <li>Email: support@gruham.in</li>
        <li>Phone: +91 9664527720</li>
        <li>Response Time: Within 30 days.</li>
      </ul>
    </LegalLayout.Section>

    <LegalLayout.Section id="grievance" title="12. Grievance Redressal">
      <p>
        Submit complaints in writing to support@gruham.in. We acknowledge
        within 24 hours and investigate within 15 days. If unsatisfied, you may
        escalate to the National Consumer Disputes Redressal Commission.
      </p>
    </LegalLayout.Section>

    <LegalLayout.Section id="transfers" title="13. International Data Transfers">
      <p>
        Your personal data is primarily stored and processed in India. We may
        transfer data internationally for cloud hosting or third-party services
        with appropriate safeguards.
      </p>
    </LegalLayout.Section>

    <LegalLayout.Section id="changes" title="14. Changes to This Privacy Policy">
      <p>
        We may update this Privacy Policy from time to time. Material changes
        will be notified via prominent notice on the Platform or via email.
      </p>
    </LegalLayout.Section>

    <LegalLayout.Section id="definitions" title="15. Definitions">
      <ul className="list-disc pl-5 space-y-1">
        <li><strong>Personal Data:</strong> Any information relating to an identified or identifiable natural person.</li>
        <li><strong>Sensitive Personal Data:</strong> Passwords, financial data, health, biometric data, etc.</li>
        <li><strong>Processing:</strong> Any operation performed on personal data.</li>
        <li><strong>Data Controller:</strong> Gruham</li>
        <li><strong>Data Processor:</strong> Third parties processing on behalf of Gruham</li>
      </ul>
    </LegalLayout.Section>

    <LegalLayout.Section id="ack" title="16. Acknowledgment">
      <p>
        By using Gruham's Platform, you acknowledge that you have
        read, understood and agree to the terms of this Privacy Policy.
      </p>
    </LegalLayout.Section>
  </LegalLayout>
);

export default Privacy;
